import { vec3 } from "gl-matrix";
import { useEffect } from "react";

import { useAppSelector } from "app/redux-store-interactions";
import { useCameraState } from "contexts/cameraState";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { selectCrossSectionActivePlaneIdx } from "features/crossSection";
import { getCameraDir } from "features/engine2D/utils";
import { selectClippingPlanes, selectTerrain } from "features/render";
import { selectIsProcoreDesign } from "slices/explorer";
import { isCameraOnClippedSide } from "utils/math";

const transparentColor = [0, 0, 0, 0] as const;

export function useHandleClipping() {
    const {
        state: { view },
    } = useExplorerGlobals();
    const cameraState = useCameraState();
    const clipping = useAppSelector(selectClippingPlanes);
    const terrain = useAppSelector(selectTerrain);
    const isProcoreDesign = useAppSelector(selectIsProcoreDesign);
    const crossSectionActivePlaneIdx = useAppSelector(selectCrossSectionActivePlaneIdx);

    useEffect(() => {
        if (!view) {
            return;
        }

        const cameraDir = getCameraDir(view.renderState.camera.rotation);
        const isOrtho = view.renderState.camera.kind === "orthographic";
        const cameraPos = cameraState?.position ?? view.renderState.camera.position;

        view.modifyRenderState({
            clipping: {
                ...clipping,
                draw: true,
                planes: clipping.planes.map(({ baseW: _baseW, ...plane }, idx) => {
                    // If plane is parallel to camera in ortho mode - we'll just see whole screen tinted with plane color,
                    // which doesn't really bring any value, so turn off plane color in this case
                    const isParallelToCamera =
                        isOrtho &&
                        Math.abs(
                            vec3.dot(
                                cameraDir,
                                vec3.fromValues(plane.normalOffset[0], plane.normalOffset[1], plane.normalOffset[2]),
                            ),
                        ) > 0.999;

                    const cameraIsOnClippedSide = isCameraOnClippedSide(cameraPos, plane.normalOffset);
                    const blackOutline = isProcoreDesign && cameraIsOnClippedSide;
                    const hideOutline =
                        !cameraIsOnClippedSide && !isParallelToCamera && idx !== crossSectionActivePlaneIdx;
                    return {
                        ...plane,
                        color: isParallelToCamera || !plane.showPlane ? transparentColor : plane.color,
                        outline: {
                            enabled: !hideOutline && (blackOutline || (clipping.outlines && plane.outline.enabled)),
                            lineColor: blackOutline ? [0, 0, 0] : plane.outline.color,
                        },
                    };
                }),
            },
            terrain: { asBackground: clipping.planes.some((p) => p.showPlane) ? false : terrain.asBackground },
            triangles: {
                doubleSided: clipping.planes.length > 0,
            },
        });
    }, [view, clipping, terrain, isProcoreDesign, cameraState, crossSectionActivePlaneIdx]);
}
